// Offline mode (OFFLINE-PLAN.md): which requests are the daily jobs that may be made offline, which right each needs, its
// plain label, and the conflict check at sync. Plain JS, no imports - the browser, the server-free tests use it.

// ---------------- requests ----------------
// the login's own calls (log in, token refresh, sign out, user): never cached or queued
export const isAuthUrl = url => /\/auth\/v1\//.test(String(url || ''))
// a Supabase request -> { kind: 'read' | 'write' | 'rpc' | 'upload' | 'other', table, id, query, path }
export function parseRequest(url, method = 'GET') {
  let u
  try { u = new URL(String(url)) } catch (e) { return { kind: 'other' } }
  if (isAuthUrl(u.href)) return { kind: 'other', auth: true }
  const m = String(method || 'GET').toUpperCase()
  const rest = u.pathname.match(/\/rest\/v1\/(rpc\/)?([A-Za-z0-9_]+)\/?$/)
  if (rest) {
    const q = Object.fromEntries(u.searchParams.entries())
    if (rest[1]) return { kind: 'rpc', fn: rest[2], method: m }
    const idm = String(q.id || '').match(/^eq\.(.+)$/)
    return { kind: m === 'GET' || m === 'HEAD' ? 'read' : 'write', table: rest[2], method: m, id: idm ? decodeURIComponent(idm[1]) : null, query: q }
  }
  const st = u.pathname.match(/\/storage\/v1\/object\/([^/]+)\/(.+)$/)
  if (st && m === 'POST' && !/^(sign|public|authenticated|list|move|copy)$/.test(st[1])) return { kind: 'upload', bucket: st[1], path: decodeURIComponent(st[2]), method: m }
  return { kind: 'other', method: m }
}
// the vessel of a request (client_id=eq.<uuid> filter, or in the body)
export function vesselOf(query, body) {
  const q = query && String(query.client_id || '').match(/^eq\.([0-9a-f-]{36})$/i)
  if (q) return q[1]
  const b = Array.isArray(body) ? body[0] : body
  return (b && b.client_id) || null
}
// the login of a request: the "sub" of its token (no network needed)
export function userOfToken(authHeader) {
  try {
    const t = String(authHeader || '').replace(/^Bearer\s+/i, '').split('.')[1]
    if (!t) return null
    const json = typeof atob === 'function' ? atob(t.replace(/-/g, '+').replace(/_/g, '/')) : Buffer.from(t, 'base64').toString('utf8')
    const p = JSON.parse(decodeURIComponent(escape(json)))
    return p.role === 'authenticated' && p.sub ? p.sub : null
  } catch (e) { return null }
}

// ---------------- the daily jobs (phase 1) ----------------
const STATUS_FIELDS = ['status', 'status_reason', 'status_by', 'out_of_service_since']
export const OFFLINE_JOBS = {
  // Communication (sql/comms.sql): any login, on its own row / in its own conversation - the database decides (any: true)
  comms_notice_recipients: { any: true, methods: ['PATCH'], what: 'Acknowledgement', fields: ['acked'] },
  comms_messages: { any: true, methods: ['POST'], what: 'Message' },
  comms_record_comments: { any: true, methods: ['POST'], what: 'Comment' },
  todo_items: { module: 'todo', methods: ['POST', 'PATCH'], what: 'To Do' },
  maintenance_log: { module: 'maintenance', methods: ['POST'], what: 'Maintenance log' },
  hours_rest: { module: 'hours_rest', methods: ['POST', 'PATCH'], what: 'Hours of rest', own: true },
  equipment: { module: 'equipment', methods: ['PATCH'], what: 'Equipment status', fields: STATUS_FIELDS },
  // Contingency Plans (sql/contingency.sql): a finished EMERGENCY run and its drafts - Checklists View is enough (level read)
  checklist_instances: { module: 'checklists', methods: ['POST'], what: 'Emergency run / drill', level: 'read', only: b => (b.emergency === true && b.drill !== true) || (b.drill === true && b.emergency !== true) },
  emergency_records: { module: 'checklists', methods: ['POST'], what: 'Emergency record draft', level: 'read', only: b => b.status === 'draft' },
  // Service package: the technician's phone (right "service_jobs": only a technician login has it)
  work_orders: { module: 'service_jobs', methods: ['PATCH'], what: 'Job', fields: ['status', 'notes'] },
  work_order_tasks: { module: 'service_jobs', methods: ['PATCH'], what: 'Job checklist', fields: ['done'] },
  work_order_parts: { module: 'service_jobs', methods: ['POST'], what: 'Part request' },
  work_order_photos: { module: 'service_jobs', methods: ['POST'], what: 'Job photo' },
  work_order_time: { module: 'service_jobs', methods: ['POST', 'PATCH'], what: 'Job clock' },
  technician_shifts: { module: 'service_jobs', methods: ['POST', 'PATCH'], what: 'Shift clock' },
}
export const OFFLINE_RPC = { hc_stock_change: { module: 'inventory', what: 'Stock change' } }
// uploads: only To Do photos (snags) - storage paths "todo/<vessel>/..."
// a table whose rows this login reads through a view (no prices): a queued new row shows in that view's cached answer too
export const OFFLINE_VIEW_OF = { work_order_parts: 'work_order_parts_tech' }
export const OFFLINE_UPLOAD_AREAS = { todo: 'todo', service_jobs: 'service_jobs' }
export const NEEDS_CONNECTION = 'You are offline - this needs a connection. Offline you can: add / tick To Do tasks and snags (with photos), log maintenance, record your hours of rest, set equipment out of / back in service, change stock and run an emergency Contingency Plan or a drill of one.'

const nrm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
// may this change be made offline by this login? -> { ok, module, why }
// rights: { can(module, level), isCrew, crewName }; cachedRow: the row a PATCH changes (from the cache), if known
export function offlineJob(req, body, rights, cachedRow) {
  const r = rights || {}
  const can = m => (typeof r.can === 'function' ? r.can(m, 'write') : false)
  if (req.kind === 'rpc') {
    const j = OFFLINE_RPC[req.fn]
    if (!j) return { ok: false, why: NEEDS_CONNECTION }
    if (!can(j.module)) return { ok: false, module: j.module, why: 'Your login cannot change the Inventory - not saved.' }
    return { ok: true, module: j.module }
  }
  if (req.kind === 'upload') {
    const area = String(req.path || '').split('/')[0]
    const m = OFFLINE_UPLOAD_AREAS[area]
    if (!m) return { ok: false, why: NEEDS_CONNECTION }
    if (!can(m)) return { ok: false, module: m, why: 'Your login cannot change the To Do lists - not saved.' }
    return { ok: true, module: m }
  }
  if (req.kind !== 'write') return { ok: false, why: NEEDS_CONNECTION }
  const j = OFFLINE_JOBS[req.table]
  if (!j || !j.methods.includes(req.method)) return { ok: false, why: NEEDS_CONNECTION }
  if (req.method === 'PATCH' && !req.id) return { ok: false, why: NEEDS_CONNECTION }   // one record at a time
  if (j.fields && body && Object.keys(body).some(k => !j.fields.includes(k))) return { ok: false, why: NEEDS_CONNECTION }
  if (j.only && !(Array.isArray(body) ? body : [body || {}]).every(b => b && j.only(b))) return { ok: false, why: NEEDS_CONNECTION }
  if (j.any) return { ok: true, module: 'comms', any: true }   // Communication
  if (j.level === 'read' ? (typeof r.can === 'function' && r.can(j.module, 'read')) : can(j.module)) return { ok: true, module: j.module, level: j.level || 'write' }
  // hours of rest: a crew login may always record its OWN
  if (j.own && r.isCrew) {
    const rows = Array.isArray(body) ? body : [body || {}]
    const who = rows.map(b => b.crew_member != null ? b.crew_member : cachedRow && cachedRow.crew_member)
    if (who.every(w => w && nrm(w) === nrm(r.crewName))) return { ok: true, module: j.module }
    return { ok: false, module: j.module, why: 'Your login can only record your own hours of rest - not saved.' }
  }
  return { ok: false, module: j.module, why: 'Your login cannot change ' + j.what + ' - not saved.' }
}

// ---------------- at sync: checked again (the queue may be hours old) ----------------
export const NOT_FROM_QUEUE = 'Not saved: this change cannot be sent from the offline queue (only the daily offline jobs can) - nothing was written.'
export const RIGHTS_GONE = 'Not saved: your login can no longer make this change (its rights were changed or it was deactivated after this was made offline) - nothing was written.'
export const LOGIN_RENEW = 'Changes are waiting: log in again to send them (this login could not be renewed - it may have been deactivated). Nothing was sent or lost.'
// a queued change just before it is sent -> { ok, why }. Shape: only a daily job of phase 1, to this server (a queue edited
// in the browser's storage cannot send another write, or the login's token to another address). Rights: the login's
// rights NOW (null = not known yet -> the database decides alone); own hours of rest: the database decides.
export function recheck(item, rights, baseUrl) {
  const it = item || {}
  if (baseUrl && !String(it.url || '').startsWith(String(baseUrl).replace(/\/+$/, '') + '/')) return { ok: false, why: NOT_FROM_QUEUE }
  const req = parseRequest(it.url, it.method)
  let body = null
  try { body = it.body ? JSON.parse(it.body) : null } catch (e) { return { ok: false, why: NOT_FROM_QUEUE } }
  const job = offlineJob(req, body, { can: () => true }, null)
  const table = req.kind === 'rpc' ? 'inventory' : req.table || null
  if (!job.ok || req.kind !== it.kind || (it.table && table && it.table !== table)) return { ok: false, why: NOT_FROM_QUEUE }
  if (job.any) return { ok: true }   // Communication: the database decides
  const r = rights
  if (!r || typeof r.can !== 'function') return { ok: true }
  if (r.vesselId && it.vessel && String(r.vesselId) !== String(it.vessel)) return { ok: true }   // rights of another vessel: the database decides
  if (r.can(job.module, job.level || 'write')) return { ok: true }
  if (req.table === 'hours_rest' && r.isCrew) return { ok: true }                                // own hours: the database decides
  return { ok: false, why: RIGHTS_GONE }
}
// the change is already on the server (sent before and its answer lost, or sent by another tab): nothing to send again
export function alreadyApplied(item, server, body) {
  if (!item || !server || !body) return false
  if (item.kind === 'rpc') return body.p_quantity != null && server.quantity != null && Number(server.quantity) === Number(body.p_quantity)
  if (item.method !== 'PATCH') return false
  const keys = Object.keys(body)
  return keys.length > 0 && keys.every(k => k in server && same(server[k], body[k]))
}
// the server's "this offline change was already saved" (sql/security-1.sql), or "this new record already exists"
export const isDuplicate = (status, j, item) => status === 409 && (/hc_offline_duplicate/.test(String((j && (j.hint || j.details)) || '')) || item.method === 'POST' || !!item.upload)
// a refusal by the row rules (RLS)
export const isRightsRefusal = (status, j) => status === 403 || (j && j.code === '42501') || /row-level security|permission denied/i.test(String((j && j.message) || ''))

// a plain label for the "waiting to sync" list
const cut = (s, n = 60) => { s = String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n - 1) + '…' : s }
export function changeLabel(req, body, cachedRow) {
  const b = (Array.isArray(body) ? body[0] : body) || {}, row = cachedRow || {}
  if (req.table === 'comms_notice_recipients') return 'Acknowledged: ' + cut((row.title) || 'notice')   // Communication
  if (req.table === 'comms_messages') return 'Message: ' + cut(b.body || 'photo')
  if (req.table === 'comms_record_comments') return 'Comment: ' + cut(b.body)
  if (req.kind === 'rpc' && req.fn === 'hc_stock_change') return 'Stock: ' + cut(row.part_name || 'item') + ' ' + (row.quantity != null ? row.quantity + ' → ' : '→ ') + b.p_quantity + (b.p_reason ? ' (' + String(b.p_reason).replace(/_/g, ' ') + ')' : '')
  if (req.kind === 'upload') return String(req.path || '').startsWith('service_jobs/') ? 'Photo for a job' : 'Photo for a To Do task'
  if (req.table === 'work_orders') return (row.title ? cut(row.title) + ': ' : 'Job: ') + (b.status || 'notes')
  if (req.table === 'work_order_tasks') return 'Job checklist: "' + cut(row.text || 'line') + '"' + (b.done ? ' ticked' : ' un-ticked')
  if (req.table === 'work_order_parts') return 'Part requested: ' + cut(b.part_name)
  if (req.table === 'work_order_photos') return 'Job photo'
  if (req.table === 'work_order_time') return b.ended_at ? 'Job clock: out' : 'Job clock: in'
  if (req.table === 'technician_shifts') return b.ended_at ? 'Shift: clocked out' : 'Shift: clocked in'
  if (req.table === 'todo_items') {
    if (req.method === 'POST') return 'To Do: added "' + cut(b.text) + '"'
    const name = '"' + cut(row.text || 'task') + '"'
    if ('done' in b) return 'To Do: ' + name + (b.done ? ' ticked' : ' un-ticked')
    if ('photos' in b) return 'To Do: photo added to ' + name
    return 'To Do: ' + name + ' changed'
  }
  if (req.table === 'maintenance_log') return 'Maintenance log: ' + cut(b.task || b.work_description || 'entry') + (b.equipment_uid ? ' (' + b.equipment_uid + ')' : '')
  if (req.table === 'hours_rest') return 'Hours of rest: ' + cut(b.crew_member || row.crew_member || '') + ' ' + (b.month || row.month || '')
  if (req.table === 'equipment') return cut(row.name || 'Equipment') + ': ' + (b.status || 'status') + (b.status_reason ? ' - ' + cut(b.status_reason, 40) : '')
  if (req.table === 'checklist_instances') return (b.drill ? 'Drill saved: ' : 'Emergency run saved: ') + cut(String(b.general_notes || '').replace(/^(EMERGENCY RUN|DRILL) - /, '').split('.')[0] || 'checklist')
  if (req.table === 'emergency_records') return 'Draft: ' + cut(b.title || 'emergency record')
  return (req.table || 'Change') + ' ' + (req.method || '')
}

// ---------------- conflicts ----------------
const same = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return (a == null || a === '') && (b == null || b === '')
  if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b)
  return String(a) === String(b)
}
// the fields this change sets that someone else changed on the server since the device read the record:
// server value differs from the value the change was based on AND from the new value -> [{ field, base, server, mine }]
export function conflicts(base, server, patch) {
  const out = []
  for (const [f, mine] of Object.entries(patch || {})) {
    if (!base || !(f in base)) continue          // not known when it was made: nothing to compare
    if (!same(server[f], base[f]) && !same(server[f], mine)) out.push({ field: f, base: base[f], server: server[f], mine })
  }
  return out
}
// stock: the quantity on the server is not the one the change started from
export function stockConflict(baseQty, serverQty) {
  return baseQty != null && serverQty != null && Number(baseQty) !== Number(serverQty)
}
// "apply my change to the current stock": the same difference on top of the server's quantity (below 0: the database refuses it)
export const restock = (baseQty, mineQty, serverQty) => Number(serverQty) + (Number(mineQty) - Number(baseQty))
